import React, { useEffect, useState } from "react";
import { Alert, Button, Popconfirm, Table, message } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faRotateRight, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  useDeleteExternalMutation,
  useLazyGetExternalDetailsQuery,
  usePutExternalRoleMutation,
} from "../../../redux/externals/externalsAPI";
import GETJwtToken from "../../../redux/authentication/authentication";
import { useScreenPermission } from "../../../customHooks/useScopedPermission";
import { Permission, hasPermission } from "../../../types/permissions";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
import type { RootState } from "../../../app/store";
import type {
  ExternalDetail,
  ExternalMutationResult,
} from "../../../interfaces/External.interface";
import ApiClientRoleSelect from "./ApiClientRoleSelect";
import ApiClientCreateModal from "./ApiClientCreateModal";
import ApiClientSecretModal from "./ApiClientSecretModal";
import { useApiClientRoles } from "./apiClientRoles";
import apiClientErrorMessage from "./apiClientErrors";

/**
 * The API clients (LiftCloud Externals) of the selected organization: the
 * credentials another system uses on the external API, and the role each one
 * acts as there (LIFT-1098).
 *
 * The organization is the header's selection, falling back to the signed-in
 * user's own. Listing needs externals-view (the screen permission); the
 * create, role and delete controls each appear only with their own
 * permission, and LiftCloud checks all of them again.
 */
function ApiClientsContent() {
  const [t] = useTranslation("global");
  const { theme, globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const held = permissionSet ?? [];
  const canCreate = hasPermission(Permission.ExternalsCreate, held);
  const canUpdate = hasPermission(Permission.ExternalsUpdate, held);
  const canDelete = hasPermission(Permission.ExternalsDelete, held);

  const orgId: string = globalOrganizationId || userLogged?.organizationId || "";

  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<ExternalMutationResult | null>(null);
  const [savingRoleFor, setSavingRoleFor] = useState("");

  const [triggerList, { data, isFetching, error: listError }] = useLazyGetExternalDetailsQuery();
  const [putExternalRole] = usePutExternalRoleMutation();
  const [deleteExternal] = useDeleteExternalMutation();
  const { roles, isFetching: rolesLoading } = useApiClientRoles(orgId);

  const load = async () => {
    if (!orgId) return;
    const token = await GETJwtToken();
    triggerList({ token, orgId });
  };

  useEffect(() => {
    load();
  }, [orgId]);

  useEffect(() => {
    setError(listError ? apiClientErrorMessage(listError, t("apiClients.genericError")) : "");
  }, [listError]);

  const changeRole = async (row: ExternalDetail, roleId: string) => {
    setError("");
    setSavingRoleFor(row.id);
    try {
      const token = await GETJwtToken();
      await putExternalRole({ token, id: row.id, name: row.name, roleId }).unwrap();
      message.success(t("apiClients.roleUpdated"));
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.genericError")));
    } finally {
      setSavingRoleFor("");
    }
  };

  const remove = async (row: ExternalDetail) => {
    setError("");
    try {
      const token = await GETJwtToken();
      await deleteExternal({ token, id: row.id }).unwrap();
      message.success(t("apiClients.deleted"));
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.genericError")));
    }
  };

  const columns = [
    {
      title: t("apiClients.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("apiClients.clientId"),
      dataIndex: "client_id",
      key: "client_id",
      render: (clientId: string) => <code>{clientId}</code>,
    },
    {
      title: t("apiClients.role"),
      key: "role",
      render: (_: unknown, row: ExternalDetail) =>
        canUpdate && row.org_id ? (
          <ApiClientRoleSelect
            testId={`api-client-role-${row.id}`}
            roles={roles}
            value={row.role_id}
            loading={rolesLoading || savingRoleFor === row.id}
            disabled={savingRoleFor === row.id}
            onChange={(roleId) => changeRole(row, roleId)}
          />
        ) : (
          <span data-testid={`api-client-role-name-${row.id}`}>
            {row.role_name ?? row.role_id ?? t("apiClients.noRole")}
          </span>
        ),
    },
    ...(canDelete
      ? [
          {
            title: t("apiClients.actions"),
            key: "actions",
            render: (_: unknown, row: ExternalDetail) => (
              <Popconfirm
                title={t("apiClients.deleteConfirm", { name: row.name })}
                okText={t("apiClients.delete")}
                cancelText={t("apiClients.cancel")}
                okButtonProps={
                  { danger: true, "data-testid": `api-client-delete-confirm-${row.id}` } as Record<
                    string,
                    unknown
                  >
                }
                onConfirm={() => remove(row)}
              >
                <Button
                  danger
                  data-testid={`api-client-delete-${row.id}`}
                  icon={<FontAwesomeIcon icon={faTrash} className="generalStyles__mrFix" />}
                >
                  {t("apiClients.delete")}
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={`layout__contentPage__${theme}`} data-testid="api-clients-page">
      <div className="generalStyles__pageHeader">
        {canCreate && orgId && (
          <div className="generalStyles__mlFix mt-1">
            <Button
              type="primary"
              data-testid="api-client-create"
              icon={<FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />}
              onClick={() => setCreateOpen(true)}
            >
              {t("apiClients.create")}
            </Button>
          </div>
        )}
        <div className="generalStyles__mlFix mt-1">
          <Button
            data-testid="api-client-refresh"
            icon={<FontAwesomeIcon icon={faRotateRight} className="generalStyles__mrFix" />}
            onClick={load}
          >
            {t("apiClients.refresh")}
          </Button>
        </div>
      </div>
      <p className="mt-3">{t("apiClients.description")}</p>
      {error && (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError("")}
          className="mb-3"
          data-testid="api-clients-error"
        />
      )}
      {orgId ? (
        <Table
          data-testid="api-clients-table"
          rowKey="id"
          loading={isFetching}
          dataSource={data ?? []}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t("apiClients.empty") }}
          scroll={{ x: 900 }}
        />
      ) : (
        <Alert type="info" showIcon message={t("apiClients.noOrganization")} />
      )}
      <ApiClientCreateModal
        open={createOpen}
        orgId={orgId}
        roles={roles}
        rolesLoading={rolesLoading}
        onClose={() => setCreateOpen(false)}
        onCreated={(result) => {
          setCreateOpen(false);
          setCreated(result);
        }}
      />
      <ApiClientSecretModal created={created} onClose={() => setCreated(null)} />
    </div>
  );
}

function ApiClientsPage() {
  // SCREEN_PERMISSION["api-clients"], the declaration the sidebar link reads.
  const allowed = useScreenPermission("api-clients");
  return allowed ? <ApiClientsContent /> : <UnauthorizedPage />;
}

export default ApiClientsPage;
